import { useMemo, useState } from 'react';
import { Search, Plus, Minus, Trash2, ShoppingBag, AlertTriangle, X } from 'lucide-react';
import { Button, Card, Input, Pill, useToast } from '@nako/ui';
import { formatMoney, stockOnHand, dispenseTotals, lineTotals, scheduleLabel } from '@nako/core';
import type { Business, Profile, PharmacyDrug, DispensePaymentMethod } from '@nako/types';
import { isControlledSchedule } from '@nako/types';
import { supabase } from '@/lib/supabase';
import { playChime } from '@/lib/chime';
import { postDispense, type DispenseCartLine } from '@/lib/pharmacy-postings';
import type { PharmacyData } from '@/pages/Pharmacy';

interface CartItem {
  drug: PharmacyDrug;
  qty: number;
  unitPrice: number;
  directions: string;
}

interface PatientLite {
  id: string;
  first_name: string;
  last_name: string;
  medical_aid_member_number: string | null;
}

const PAYMENT_METHODS: Array<{ id: DispensePaymentMethod; label: string }> = [
  { id: 'cash', label: 'Cash' },
  { id: 'card', label: 'Card' },
  { id: 'mobile_money', label: 'Mobile money' },
  { id: 'medical_aid', label: 'Medical aid' },
  { id: 'account', label: 'On account' },
];

export function DispensePOS({
  business,
  profile,
  data,
}: {
  business: Business;
  profile: Profile | null;
  data: PharmacyData;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [payment, setPayment] = useState<DispensePaymentMethod>('cash');
  const [counselled, setCounselled] = useState(false);
  const [prescriber, setPrescriber] = useState('');
  const [prescriptionRef, setPrescriptionRef] = useState('');
  const [patient, setPatient] = useState<PatientLite | null>(null);
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState<PatientLite[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const active = data.drugs.filter((d) => d.is_active);
    if (!q) return active.slice(0, 8);
    return active
      .filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.generic_name ?? '').toLowerCase().includes(q) ||
          (d.barcode ?? '').toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [search, data.drugs]);

  const splits = cart.map((c) =>
    lineTotals(c.qty, c.unitPrice, business.vat_registered ? c.drug.vat_treatment : 'exempt'),
  );
  const totals = dispenseTotals(splits);

  const hasControlled = cart.some((c) => isControlledSchedule(c.drug.schedule));
  const needsPatient = hasControlled && !patient && !prescriber.trim();

  function addToCart(drug: PharmacyDrug) {
    setCart((prev) => {
      const existing = prev.find((c) => c.drug.id === drug.id);
      if (existing) {
        return prev.map((c) => (c.drug.id === drug.id ? { ...c, qty: c.qty + 1 } : c));
      }
      return [...prev, { drug, qty: 1, unitPrice: drug.sale_price ?? 0, directions: '' }];
    });
    setSearch('');
  }

  function setQty(drugId: string, qty: number) {
    setCart((prev) =>
      prev.map((c) => (c.drug.id === drugId ? { ...c, qty: Math.max(0.001, qty) } : c)),
    );
  }

  function removeLine(drugId: string) {
    setCart((prev) => prev.filter((c) => c.drug.id !== drugId));
  }

  async function searchPatients(q: string) {
    setPatientQuery(q);
    if (q.trim().length < 2) {
      setPatientResults([]);
      return;
    }
    const { data: rows } = await supabase
      .from('patients')
      .select('id, first_name, last_name, medical_aid_member_number')
      .eq('business_id', business.id)
      .is('deleted_at', null)
      .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
      .limit(6);
    setPatientResults((rows ?? []) as PatientLite[]);
  }

  async function dispense() {
    if (cart.length === 0) return;
    if (needsPatient) {
      toast.show('Controlled item needs a patient or prescriber.', { tone: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const lines: DispenseCartLine[] = cart.map((c) => ({
        drug: c.drug,
        qty: c.qty,
        unitPrice: c.unitPrice,
        directions: c.directions || undefined,
      }));
      const res = await postDispense({
        businessId: business.id,
        userId: profile?.id ?? null,
        patientId: patient?.id ?? null,
        prescriberName: prescriber.trim() || null,
        prescriptionRef: prescriptionRef.trim() || null,
        paymentMethod: payment,
        counselled,
        cart: lines,
        vatRegistered: business.vat_registered,
      });
      if (!res.ok) {
        toast.show(res.error ?? 'Dispense failed.', { tone: 'error' });
        return;
      }
      playChime();
      toast.show(`Dispensed ${res.dispense_no}`, { tone: 'success' });
      setCart([]);
      setPatient(null);
      setPrescriber('');
      setPrescriptionRef('');
      setCounselled(false);
      setPayment('cash');
      await data.reload();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_minmax(320px,380px)]">
      {/* Drug picker */}
      <div>
        <Input
          leftAddon={<Search size={16} />}
          placeholder="Search drug by name, generic or barcode…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="mt-3 grid gap-2">
          {results.length === 0 && (
            <p className="text-muted px-1 py-6 text-center text-sm">
              {data.drugs.length === 0
                ? 'No drugs yet — add them in the Formulary tab.'
                : 'No match.'}
            </p>
          )}
          {results.map((d) => {
            const onHand = stockOnHand(data.batchesByDrug.get(d.id) ?? []);
            const low = onHand <= d.reorder_level;
            return (
              <button
                key={d.id}
                onClick={() => addToCart(d)}
                disabled={onHand <= 0}
                className="rounded-btn border-line bg-paper hover:bg-paper-2 flex items-center justify-between gap-3 border p-3 text-left transition-colors disabled:opacity-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-ink truncate font-medium">{d.name}</span>
                    {d.strength && <span className="text-muted text-xs">{d.strength}</span>}
                    {isControlledSchedule(d.schedule) && <Pill tone="red">{d.schedule}</Pill>}
                  </div>
                  <div className="text-muted mt-0.5 text-xs">
                    {d.generic_name ?? d.form ?? ''} · {formatMoney(d.sale_price ?? 0)} ·{' '}
                    <span className={low ? 'text-[#991B1B]' : ''}>
                      {onHand} {d.dispense_unit}
                    </span>
                  </div>
                </div>
                <Plus size={18} className="text-muted shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Cart */}
      <Card className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <ShoppingBag size={18} className="text-emerald-600" />
          <h2 className="font-display text-lg">Dispense</h2>
        </div>

        {cart.length === 0 && <p className="text-muted py-6 text-center text-sm">Cart is empty.</p>}

        {cart.map((c) => {
          const onHand = stockOnHand(data.batchesByDrug.get(c.drug.id) ?? []);
          const over = c.qty > onHand;
          return (
            <div key={c.drug.id} className="border-line border-b pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{c.drug.name}</span>
                    {isControlledSchedule(c.drug.schedule) && (
                      <Pill tone="red">{scheduleLabel(c.drug.schedule)}</Pill>
                    )}
                  </div>
                  <span className="text-muted text-xs">{formatMoney(c.unitPrice)} each</span>
                </div>
                <button onClick={() => removeLine(c.drug.id)} aria-label="Remove">
                  <Trash2 size={15} className="text-muted hover:text-[#991B1B]" />
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => setQty(c.drug.id, c.qty - 1)}
                  className="rounded-btn border-line flex h-8 w-8 items-center justify-center border"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  inputMode="decimal"
                  value={c.qty}
                  onChange={(e) => setQty(c.drug.id, Number(e.target.value) || 0)}
                  className="rounded-btn border-line h-8 w-16 border text-center text-sm"
                />
                <button
                  onClick={() => setQty(c.drug.id, c.qty + 1)}
                  className="rounded-btn border-line flex h-8 w-8 items-center justify-center border"
                >
                  <Plus size={14} />
                </button>
                <span className="ml-auto text-sm font-medium">
                  {formatMoney(c.qty * c.unitPrice)}
                </span>
              </div>
              {over && (
                <p className="mt-1 flex items-center gap-1 text-xs text-[#991B1B]">
                  <AlertTriangle size={12} /> Only {onHand} {c.drug.dispense_unit} in unexpired
                  stock
                </p>
              )}
            </div>
          );
        })}

        {/* Patient / prescriber */}
        {cart.length > 0 && (
          <div className="grid gap-2">
            {patient ? (
              <div className="rounded-btn bg-paper-2 flex items-center justify-between px-3 py-2 text-sm">
                <span>
                  {patient.first_name} {patient.last_name}
                  {patient.medical_aid_member_number && (
                    <span className="text-muted"> · {patient.medical_aid_member_number}</span>
                  )}
                </span>
                <button onClick={() => setPatient(null)} aria-label="Clear patient">
                  <X size={15} className="text-muted" />
                </button>
              </div>
            ) : (
              <div>
                <Input
                  placeholder="Link patient (optional; required for controlled)…"
                  value={patientQuery}
                  onChange={(e) => void searchPatients(e.target.value)}
                />
                {patientResults.length > 0 && (
                  <div className="border-line rounded-btn mt-1 grid gap-1 border p-1">
                    {patientResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setPatient(p);
                          setPatientResults([]);
                          setPatientQuery('');
                        }}
                        className="rounded-btn hover:bg-paper-2 px-2 py-1.5 text-left text-sm"
                      >
                        {p.first_name} {p.last_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {hasControlled && (
              <>
                <Input
                  placeholder="Prescriber name"
                  value={prescriber}
                  onChange={(e) => setPrescriber(e.target.value)}
                />
                <Input
                  placeholder="Script / prescription ref"
                  value={prescriptionRef}
                  onChange={(e) => setPrescriptionRef(e.target.value)}
                />
              </>
            )}

            <div className="flex flex-wrap gap-1">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setPayment(m.id)}
                  className={
                    'rounded-pill border px-2.5 py-1 text-xs font-medium ' +
                    (payment === m.id ? 'border-ink bg-ink text-paper' : 'border-line text-muted')
                  }
                >
                  {m.label}
                </button>
              ))}
            </div>

            <label className="text-muted flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={counselled}
                onChange={(e) => setCounselled(e.target.checked)}
              />
              Patient counselled on use
            </label>
          </div>
        )}

        {/* Totals */}
        {cart.length > 0 && (
          <div className="border-line mt-1 border-t pt-3 text-sm">
            <div className="text-muted flex justify-between">
              <span>Subtotal</span>
              <span>{formatMoney(totals.subtotal)}</span>
            </div>
            <div className="text-muted flex justify-between">
              <span>VAT</span>
              <span>{formatMoney(totals.vat_amount)}</span>
            </div>
            <div className="mt-1 flex justify-between text-base font-semibold">
              <span>Total</span>
              <span>{formatMoney(totals.total)}</span>
            </div>
          </div>
        )}

        {needsPatient && (
          <p className="flex items-center gap-1 text-xs text-[#991B1B]">
            <AlertTriangle size={12} /> Controlled item — capture a patient or prescriber first.
          </p>
        )}

        <Button
          onClick={() => void dispense()}
          disabled={cart.length === 0 || submitting || needsPatient}
        >
          {submitting ? 'Dispensing…' : `Dispense ${formatMoney(totals.total)}`}
        </Button>
      </Card>
    </div>
  );
}
